/**
 * Website-local validation helpers for the candidate profile form
 * (My Profile + Profile Completion modal). Mirrors the shape of
 * `shared/src/lib/jobValidation.ts` but keeps profile-specific concerns
 * (URL normalization, field-key allowlist, total_experience_months → years_experience
 * translation) out of the shared package because there's only one consumer.
 */
import { ApiError } from './apiClient';

export type ProfileFieldKey =
  | 'full_name'
  | 'phone'
  | 'linkedin_url'
  | 'github_url'
  | 'portfolio_url'
  | 'current_role'
  | 'current_company'
  | 'years_experience'
  | 'summary'
  | 'experience_level_id'
  | 'notice_period_id'
  | 'expected_salary_min'
  | 'expected_salary_max'
  | 'expected_salary_currency_id'
  | 'expected_salary_pay_period_id'
  | 'willing_to_relocate'
  | 'soft_skills'
  | 'tools'
  | 'certifications'
  | 'skill_ids';

export const VALID_PROFILE_FIELD_KEYS: ReadonlySet<ProfileFieldKey> = new Set<ProfileFieldKey>([
  'full_name',
  'phone',
  'linkedin_url',
  'github_url',
  'portfolio_url',
  'current_role',
  'current_company',
  'years_experience',
  'summary',
  'experience_level_id',
  'notice_period_id',
  'expected_salary_min',
  'expected_salary_max',
  'expected_salary_currency_id',
  'expected_salary_pay_period_id',
  'willing_to_relocate',
  'soft_skills',
  'tools',
  'certifications',
  'skill_ids',
]);

export type ProfileFieldErrors = Partial<Record<ProfileFieldKey, string>>;

/**
 * Map of backend-body keys to form-state keys. The form keeps `years_experience`
 * (string, years) for input ergonomics; the wire body uses `total_experience_months`
 * (number, months). When the backend rejects the months value we want the inline
 * error to land on the years input.
 */
const BACKEND_TO_FORM_KEY: Record<string, ProfileFieldKey> = {
  total_experience_months: 'years_experience',
};

/**
 * Pull `details: [{ field, issue }]` out of an ApiError envelope and project it
 * to a `{ formFieldKey: message }` map, restricted to the allowlist. Returns an
 * empty object when the error has no field-level details — caller should then
 * fall back to a top-level banner.
 */
export function profileFieldErrorsFromApiError(err: unknown): ProfileFieldErrors {
  const errors: ProfileFieldErrors = {};
  if (!(err instanceof ApiError) || !err.details) return errors;
  for (const detail of err.details) {
    if (!detail.field || !detail.issue) continue;
    const head = detail.field.split('.')[0];
    const formKey = (BACKEND_TO_FORM_KEY[head] ?? head) as ProfileFieldKey;
    if (VALID_PROFILE_FIELD_KEYS.has(formKey) && !errors[formKey]) {
      errors[formKey] = detail.issue;
    }
  }
  return errors;
}

/**
 * Auto-prepend `https://` when a user (or the AI parser) supplies a bare host like
 * `linkedin.com/in/foo`. Empty/whitespace input collapses to an empty string so
 * `buildUpdateBody` can map it to `null`. Trims surrounding whitespace; never
 * downgrades an existing `http://` to `https://` (that's a separate policy call).
 */
export function normalizeUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Stable DOM id for the wrapper around each form field — used by the parent
 * page to scroll-to / focus the first invalid field.
 */
export function profileFieldDomId(key: ProfileFieldKey): string {
  return `profile-field-${key}`;
}

/**
 * Full name — letters/spaces/`. ' -`, ≥1 letter, 2–100 chars. Required.
 * Lifted here so SignUpPage and ProfileFormSections share the same rule
 * (signup was previously a trim-truthy gate only — see `8fedf52`-era refresh).
 * `ProfileFormSections` re-exports this name for backward compatibility.
 */
export function validateFullName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Full name is required';
  if (trimmed.length < 2) return 'Full name must be at least 2 characters';
  if (trimmed.length > 100) return 'Full name must be less than 100 characters';
  if (!/[a-zA-Z]/.test(trimmed)) return 'Full name must contain at least one letter';
  if (!/^[a-zA-Z\s.'\-]+$/.test(trimmed)) {
    return "Full name can only contain letters, spaces, and . ' -";
  }
  return null;
}

/**
 * Email — conservative client check that mirrors the backend Zod rule used on
 * candidate signup (`backend/src/modules/auth/dto.ts`:
 *   `z.string().email().max(255).trim().toLowerCase()`).
 * Strategy: validate against the trimmed-lowercased form (what the backend sees
 * after its own `.trim().toLowerCase()`), apply the same 255-char cap, and use
 * a regex that's tighter than Zod's so a client pass is essentially guaranteed
 * to also pass the server — `email()` is permissive (e.g. local part with `+`),
 * and ours allows the same characters set. Any rare client-pass / server-reject
 * still surfaces as a field-level error via the API details envelope.
 */
export function validateEmail(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return 'Email is required';
  if (normalized.length > 255) return 'Email must be less than 255 characters';
  // Local part: letters/digits/`._%+-`. Domain: labels separated by dots, TLD ≥2 letters.
  if (!/^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/.test(normalized)) {
    return 'Enter a valid email address';
  }
  return null;
}
